import { useEffect } from 'react';
import { Checkbox } from '@/components/design-system/Checkbox';
import {
  FormControl,
  FormItem,
  FormLabel,
} from '@/components/design-system/Form';

interface Choice {
  recordValue: string;
  content: string;
  ordinal: number;
  errorMessage?: string | null;
}

interface ConsentCheckboxProps {
  choices: Choice[];
  values: Record<number, string>;
  onChange: (values: Record<number, string>) => void;
  onValidationChange: (isValid: boolean) => void;
  consentSystemId?: string;
}

export const ConsentCheckbox = ({ 
  choices, 
  values, 
  onChange, 
  onValidationChange,
  consentSystemId,
}: ConsentCheckboxProps) => {
  const ADULT_SCREEN_US = 'adult_screen2_us';
  const handleCheckboxChange = (ordinal: number, checked: boolean, recordValue: string) => {
    const answer = (checked: string) => {
      return checked === 'true' ? recordValue : 'false';
    };
    if (consentSystemId === ADULT_SCREEN_US) {
      const newAnswersValues = Object.keys(values).reduce(
        (result, answerValueKey) => {
          const newValue =
            parseInt(answerValueKey, 10) === ordinal
              ? answer(String(checked))
              : 'false';

          return { ...result, [answerValueKey]: newValue };
        },
        {},
      );
      onChange(newAnswersValues);
    } else {
      const newValues = {
        ...values,
        [ordinal]: answer(String(checked))
      };
      onChange(newValues);
    }
  };

  useEffect(() => {
    const requiredChoices = choices.filter(choice => !choice.content.includes('optional'));
    if (consentSystemId === ADULT_SCREEN_US) {
      const hasRequiredSelections = requiredChoices.every(choice => String(values[choice.ordinal]) !== choice.recordValue);
      onValidationChange(hasRequiredSelections);
    } else {
      const hasRequiredSelections = requiredChoices
        .filter(choice => choice.recordValue !== 'second_parent_guardian')
        .some(choice => String(values[choice.ordinal]) !== choice.recordValue);
      onValidationChange(hasRequiredSelections);
    }
  }, [values, choices, onValidationChange]);

  return (
    <div className="mt-8 flex flex-col gap-4" data-testid="consentCheckbox-container">
      {choices.map((choice, idx) => (
        <FormItem key={choice.ordinal} className="flex items-center gap-x-3" data-testid={`consentCheckbox-item-${choice.recordValue}`}>
          <FormControl>
            <Checkbox
              id={`c${idx}`}
              checked={String(values[choice.ordinal]) === choice.recordValue || false}
              onCheckedChange={(checked) => handleCheckboxChange(choice.ordinal, checked as boolean, choice.recordValue as string)}
              data-testid={`consentCheckbox-input-${choice.recordValue}`}
            />
          </FormControl>
          <FormLabel
            htmlFor={`c${idx}`}
            className="text-sm font-medium leading-none !mt-0"
            data-testid={`consentCheckbox-label-${choice.recordValue}`}
          >
            {choice.content}
          </FormLabel>
        </FormItem>
      ))}
    </div>
  );
}; 